/** WFM suite modules — launcher tiles and per-module sub-navigation.
 *
 * A sub link is { to, label, permission, glyph }: `glyph` names its line icon (common/NavIcons.js), and `label` is its full name, which the
 * module sidebar (Layout/SuiteNavbar.js) writes under the icon in up to two lines, as Slack does ("Knowledge & Learning" goes on a second row).
 */
import { hasPermission } from '../utils/permissions';
import { ADMIN_DOOR_KEYS } from '../components/Admin/adminPages';
import iconBreakScheduler from '../assets/module-icons/break-scheduler.svg';
import iconPhm from '../assets/module-icons/productive-hours.svg';
import iconDossier from '../assets/module-icons/agent-dossier.svg';
import iconQa from '../assets/module-icons/qa-dashboard.svg';
import iconSchedule from '../assets/module-icons/schedule.svg';
import iconStatistics from '../assets/module-icons/statistics.svg';
import iconIncentives from '../assets/module-icons/incentives.svg';
import iconMeetings from '../assets/module-icons/meetings.svg';
import iconAdmin from '../assets/module-icons/admin-panel.svg';
import iconKnowledgeBase from '../assets/module-icons/knowledge-base.svg';
import iconAudit from '../assets/module-icons/audit-log.svg';

export const WFM_MODULES = [
  {
    id: 'break-scheduler',
    // Display label only (renamed 2026-10-14). The id, the path and every breaks.* permission key stay as they are.
    label: 'Break Manager',
    shortLabel: 'Breaks',
    description: 'Breaks, desks and workstreams.',
    path: '/dashboard',
    permission: 'breaks.dashboard',
    accent: '#ed1c24',
    icon: iconBreakScheduler,
    group: 'operations',
    subLinks: [
      { to: '/dashboard', label: 'Dashboard', glyph: 'grid', permission: 'breaks.dashboard' },
      { to: '/all-breaks', label: 'Breaks', glyph: 'list', permission: 'breaks.all_breaks' },
      { to: '/historic', label: 'History', glyph: 'clock', permission: 'breaks.historic' }
    ]
  },
  {
    id: 'phm',
    // Display label only (renamed 2026-10-14). The id, the path and every phm.* permission key stay as they are.
    label: 'Workforce Monitor',
    shortLabel: 'Monitor',
    description: 'Live agent status, queues and reports.',
    // Most agents hold the read-only Live board and nothing else, so they are not told there are reports.
    describe: (has) => (has('phm.view_reports') ? 'Live agent status, queues and reports.' : 'Live agent status and queues.'),
    path: '/productive-hours',
    permission: 'phm.access',
    // Live status alone (phm.view_live) opens the read-only Live board; Workforce Monitor access opens all of it.
    anyPermission: ['phm.access', 'phm.view_live'],
    accent: '#2563eb',
    icon: iconPhm,
    group: 'operations',
    subLinks: [
      { to: '/productive-hours', label: 'Live', glyph: 'pulse', permission: 'phm.access', anyPermission: ['phm.access', 'phm.view_live'] },
      { to: '/productive-hours/reports', label: 'Reports', glyph: 'chart', permission: 'phm.view_reports' },
      // Live only (the PHM roster reconcile): not in dev.
      { to: '/phm-reconcile', label: 'Reconcile', glyph: 'clipboard', permission: 'admin.panel' }
    ]
  },
  {
    id: 'dossier',
    // Display label only. The id, the path and every dossier.* permission key stay as they are.
    label: 'Company Directory',
    shortLabel: 'Dossier',
    // What it holds today: people, their positions and their profiles. Say training and documents when they are really there.
    description: 'People, positions and profiles.',
    describe: (has) => (has('dossier.view_employee_list') || has('dossier.overview')
      ? 'People, positions and profiles.'
      : 'Who works here and who reports to whom.'),
    path: '/agent-dossier',
    permission: 'dossier.access',
    accent: '#7c3aed',
    icon: iconDossier,
    group: 'operations',
    subLinks: [
      { to: '/agent-dossier', label: 'Chart', glyph: 'sitemap', permission: 'dossier.view_company_chart' },
      { to: '/agent-dossier/employees', label: 'List', glyph: 'users', permission: 'dossier.view_employee_list' },
      { to: '/agent-dossier/overview', label: 'Overview', glyph: 'dashboard', permission: 'dossier.overview' }
    ]
  },
  {
    id: 'statistics',
    // Display label only (renamed 2026-10-14). The id, the path and every stats.* permission key stay as they are.
    label: 'Insights',
    shortLabel: 'Insights',
    description: 'Agent statistics and targets.',
    path: '/statistics',
    permission: 'stats.access',
    accent: '#3b82f6',
    icon: iconStatistics,
    group: 'operations',
    subLinks: [
      { to: '/statistics', label: 'Statistics', glyph: 'chart', permission: 'stats.access' }
      // Targets moved to Incentives (2026-11-02; /statistics/targets is redirected to /incentives/targets in App.js).
      // DSAT Reviews and Quizzes are not pages any more (2026-10-28): they open at the bottom of Statistics when their cards are pressed (Bad ratings reviewed; Quiz Score and Quiz Done),
      // and the old /statistics/review and /statistics/quizzes addresses are redirected there in App.js.
    ]
  },
  {
    // Incentives (2026-10-27 as a page of Insights; a module of its own from 2026-10-30): Scorecards and Team dashboards (QA Team Lead's spec of 2026-10-26) as one
    // page, the Scoring Board, which has its own menu on the left, so the module has no tabs. An employee sees only their own score. Whose scores a person
    // reaches is decided on the server (lib/scorecards-scope.js). The scorecards.* permission keys are unchanged.
    id: 'incentives',
    label: 'Incentives',
    shortLabel: 'Incentives',
    description: 'Scorecards, feedback, team dashboards and bonuses.',
    describe: (has) => (has('scorecards.view_team') ? 'Scorecards, feedback, team dashboards and bonuses.' : 'Your own incentive scores and feedback.'),
    path: '/incentives',
    permission: 'scorecards.view_team',
    // A CS Agent holds My scores only; a team lead holds Scorecards: either opens the module.
    anyPermission: ['scorecards.view_team', 'scorecards.view_own'],
    accent: '#0d9488',
    icon: iconIncentives,
    group: 'operations',
    // Two pages (2026-11-02): the Scoring Board (it has its own menu on the left), and the Targets that used to be a page of Insights, for everyone who can open Incentives.
    subLinks: [
      { to: '/incentives', label: 'Scoring', glyph: 'clipboard', permission: 'scorecards.view_team', anyPermission: ['scorecards.view_team', 'scorecards.view_own'] },
      { to: '/incentives/targets', label: 'Targets', glyph: 'target', permission: 'scorecards.view_team', anyPermission: ['scorecards.view_team', 'scorecards.view_own'] }
    ]
  },
  {
    id: 'meetings',
    label: 'Meetings',
    shortLabel: 'Meetings',
    description: 'Who met whom, and when.',
    path: '/meetings',
    permission: 'meetings.access',
    accent: '#0891b2',
    icon: iconMeetings,
    group: 'operations',
    // The address is the view (components/Meetings/Meetings.js). From calendar needs the box to log meetings and, on the page,
    // a connected calendar (it says so when there is none); Settings holds the meeting types and the old tracker's import.
    subLinks: [
      { to: '/meetings', label: 'Counter', glyph: 'hash', permission: 'meetings.access' },
      { to: '/meetings/gaps', label: 'Gaps', glyph: 'alert', permission: 'meetings.access' },
      { to: '/meetings/schedule', label: 'Schedule', glyph: 'calendar', permission: 'meetings.access' },
      { to: '/meetings/list', label: 'List', glyph: 'chat', permission: 'meetings.access' },
      { to: '/meetings/calendar', label: 'From calendar', glyph: 'calendar-plus', permission: 'meetings.edit' },
      { to: '/meetings/settings', label: 'Settings', glyph: 'sliders', permission: 'meetings.manage_types', anyPermission: ['meetings.manage_types', 'meetings.import'] }
    ]
  },
  {
    // One module for the management reports of Customer Support: the QA reports and the Knowledge & Learning report,
    // as tabs. Each tab keeps the permission it always had (qa.access with its four report keys, knowledge.access),
    // so what a role could open before it still can, and the module card shows for a role holding either one.
    id: 'cs-reports',
    label: 'CS Management Reports',
    shortLabel: 'Reports',
    description: 'QA, Knowledge & Learning and weekly reports.',
    // Names only the reports this person can open.
    describe: (has) => {
      const parts = [
        has('qa.access') && 'QA reports',
        has('knowledge.access') && 'Knowledge & Learning',
        has('weekly_report.access') && 'the weekly report',
        has('quality.view') && 'the Quality reports'
      ].filter(Boolean);
      if (!parts.length) return 'QA, Knowledge & Learning and weekly reports.';
      const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
      return `${list.charAt(0).toUpperCase()}${list.slice(1)}.`;
    },
    path: '/cs-reports',
    permission: 'qa.access',
    anyPermission: ['qa.access', 'knowledge.access', 'weekly_report.access', 'quality.view'],
    accent: '#ED1C24',
    icon: iconQa,
    group: 'operations',
    subLinks: [
      { to: '/cs-reports/qa', label: 'QA', glyph: 'clipboard', permission: 'qa.access', anyPermission: ['qa.access', 'quality.view'] },
      { to: '/cs-reports/knowledge', label: 'Knowledge & Learning', glyph: 'book', permission: 'knowledge.access' },
      // TL Reports holds the reports a team lead sends, one view each (components/TlReports): today the weekly reports. The old
      // /cs-reports/weekly address (saved links, live's own) is redirected by App.js.
      { to: '/cs-reports/tl', label: 'TL Reports', glyph: 'report', permission: 'weekly_report.access' }
    ]
  },
  {
    id: 'schedule',
    // Display label only (renamed 2026-10-14). The id, the path and every schedule.* permission key stay as they are.
    label: 'GrafiX',
    shortLabel: 'GrafiX',
    description: 'Shifts, leave and team coverage.',
    path: '/schedule',
    permission: 'schedule.access',
    accent: '#059669',
    icon: iconSchedule,
    group: 'operations',
    subLinks: [
      { to: '/schedule', label: 'Schedule', glyph: 'calendar', permission: 'schedule.access' },
      { to: '/schedule/summary', label: 'Summary', glyph: 'table', permission: 'schedule.view_summary' },
      { to: '/schedule/heatmap', label: 'Heatmap', glyph: 'dots', permission: 'schedule.view_heatmap' },
      { to: '/schedule/settings', label: 'Settings', glyph: 'sliders', permission: 'schedule.view_settings' }
    ]
  },
  {
    // Callie, the Knowledge Base: the articles that replace Confluence (named by Ivan, 2026-10-14; "Callie (Knowledge Base)"
    // while people get used to the name). Not related to Knowledge & Learning above, a weekly report, whatever the names.
    // The id, the path and the kb.* permission keys keep the plain name.
    id: 'knowledge-base',
    label: 'Callie (Knowledge Base)',
    shortLabel: 'Callie',
    description: 'Processes, how-tos and case handling.',
    path: '/knowledge-base',
    permission: 'kb.access',
    accent: '#db2777',
    icon: iconKnowledgeBase,
    group: 'operations',
    subLinks: []
  },
  {
    id: 'admin',
    label: 'Admin Panel',
    shortLabel: 'Admin',
    description: 'Users, permissions and settings.',
    path: '/admin',
    permission: 'admin.panel',
    // Any box that opens one of its pages lets a person in; the panel then shows only those pages (adminPages.js).
    anyPermission: ADMIN_DOOR_KEYS,
    accent: '#f59e0b',
    icon: iconAdmin,
    group: 'system',
    system: true,
    subLinks: []
  },
  {
    id: 'audit-log',
    label: 'Audit Log',
    shortLabel: 'Audit',
    description: 'Who changed what, and when.',
    path: '/audit-log',
    permission: 'admin.audit',
    accent: '#64748b',
    icon: iconAudit,
    group: 'system',
    system: true,
    subLinks: []
  }
];

/** Whether this user may open a module: its own permission, or any of `anyPermission` where the module has several doors. */
export const canOpenModule = (user, mod) => (Array.isArray(mod.anyPermission)
  ? mod.anyPermission.some((key) => hasPermission(user, key))
  : hasPermission(user, mod.permission));

/** Whether this user may open one tab of a module: its own permission, or any of `anyPermission` where a tab has several doors. */
export const canOpenLink = (user, link) => (Array.isArray(link.anyPermission)
  ? link.anyPermission.some((key) => hasPermission(user, key))
  : hasPermission(user, link.permission));

/** Whether a module shows its pages as a sidebar: only when this person can open two or more of them. With one page there is nothing to choose between. */
export const hasModuleSidebar = (openableLinks) => Array.isArray(openableLinks) && openableLinks.length >= 2;

/**
 * The one line under a module's name on the home page, in the navbar menu and in search. A module whose boxes open different things
 * has a `describe(has)` that words it for what this person can open, so nobody is told about a page they cannot reach.
 */
export const moduleDescription = (user, mod) => {
  if (!mod) return '';
  if (typeof mod.describe === 'function') return mod.describe((key) => hasPermission(user, key));
  return mod.description || '';
};

/** Where a module opens for this user: its first tab they may open, or its own path for a module with no tabs. */
export const firstOpenablePath = (user, mod) => {
  const link = (mod.subLinks || []).find((l) => canOpenLink(user, l));
  return link ? link.to : mod.path;
};

/** Operational apps shown on E-OPS home and in the app switcher. */
export const isLauncherModule = (mod) => Boolean(mod) && !mod.system;

export const WFM_MODULE_GROUPS = [
  { id: 'operations', label: 'Operations' }
];

export const WFM_SYSTEM_TOOLS = WFM_MODULES.filter((m) => m.system);

export const resolveActiveModule = (pathname) => {
  if (pathname === '/home' || pathname === '/') return null;
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/all-breaks') || pathname.startsWith('/historic')) {
    return WFM_MODULES.find((m) => m.id === 'break-scheduler');
  }
  if (pathname.startsWith('/phm-reconcile') || pathname.startsWith('/productive-hours')) {
    return WFM_MODULES.find((m) => m.id === 'phm');
  }
  if (pathname.startsWith('/agent-dossier')) return WFM_MODULES.find((m) => m.id === 'dossier');
  if (pathname.startsWith('/cs-reports')) return WFM_MODULES.find((m) => m.id === 'cs-reports');
  if (pathname.startsWith('/knowledge-base')) return WFM_MODULES.find((m) => m.id === 'knowledge-base');
  if (pathname.startsWith('/statistics')) return WFM_MODULES.find((m) => m.id === 'statistics');
  if (pathname.startsWith('/meetings')) return WFM_MODULES.find((m) => m.id === 'meetings');
  if (pathname.startsWith('/incentives')) return WFM_MODULES.find((m) => m.id === 'incentives');
  if (pathname.startsWith('/schedule')) return WFM_MODULES.find((m) => m.id === 'schedule');
  if (pathname.startsWith('/admin')) return WFM_MODULES.find((m) => m.id === 'admin');
  if (pathname.startsWith('/audit-log')) return WFM_MODULES.find((m) => m.id === 'audit-log');
  return null;
};
